import { Suspense, lazy } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHead, PageLoading, Tabs, sessionDateLabel, usePageTitle } from "@/sentinel";

// Each section is its own module and mounts only while it is visible, so only its queries run.
const SafetySection = lazy(() => import("@/pages/admin/SafetySection"));
const EvidenceSection = lazy(() => import("@/pages/admin/EvidenceSection"));
const DataSection = lazy(() => import("@/pages/admin/DataSection"));
const AlertsSection = lazy(() => import("@/pages/admin/AlertsSection"));
const UsersSection = lazy(() => import("@/pages/admin/UsersSection"));

const SECTIONS = [
  { value: "safety", label: "Safety", icon: "ShieldAlert", testId: "admin-tab-safety", Component: SafetySection, blurb: "Kill switch, ENTER arming and risk guardrails." },
  { value: "evidence", label: "Evidence", icon: "Scale", testId: "admin-tab-evidence", Component: EvidenceSection, blurb: "Live calibration, auto-tune proposals and promotion review." },
  { value: "data", label: "Data", icon: "Database", testId: "admin-tab-data", Component: DataSection, blurb: "Feeds, historical store, economic calendar, scheduler and fear gauge." },
  { value: "alerts", label: "Alerts & AI", icon: "Bell", testId: "admin-tab-alerts", Component: AlertsSection, blurb: "Decision alerts, operator email, AI narratives, LLM budget and costs." },
  { value: "access", label: "Users & access", icon: "Users", testId: "admin-tab-access", Component: UsersSection, blurb: "Users, passwords and the audit log." },
];
// Links to the sections of the previous layout keep working.
const LEGACY = { controls: "safety", tuning: "evidence", research: "evidence", market: "data", scheduler: "data", ledger: "alerts" };

export default function Admin() {
  const { selectedDate, can } = useAuth();
  usePageTitle("Admin");
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const wanted = LEGACY[raw] || raw;
  const active = SECTIONS.find((s) => s.value === wanted) || SECTIONS[0];
  const setTab = (v) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", v); return n; }, { replace: true });
  const isOwner = can("owner");
  const { Component } = active;

  return (
    <div data-density="compact" className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="admin-page">
      <PageHead
        crumb={["Configure", "Admin"]}
        title="Admin"
        context={<>Session <span className="sn-num">{selectedDate ? sessionDateLabel(selectedDate) : "—"}</span> · {isOwner ? "owner controls enabled" : "read-only: every change here needs the owner role"}</>}
      />
      <Tabs tabs={SECTIONS.map(({ value, label, icon, testId }) => ({ value, label, icon, testId }))} value={active.value} onChange={setTab} label="Admin sections" />
      <p className="sn-small sn-faint" style={{ margin: 0 }}>{active.blurb}</p>
      <div role="tabpanel" aria-label={active.label} data-testid={`admin-panel-${active.value}`}>
        <Suspense fallback={<PageLoading label={`Loading ${active.label}`} />}>
          <Component />
        </Suspense>
      </div>
    </div>
  );
}
